/**
 * Is this file the HEAD of a compound upload?
 *
 * A compound head is exactly a catalog row carrying `node_id`
 * (`compound-upload-grouping-miniplan.md`) — the same fact the Acervo already
 * nests on. Keying every head behaviour off that ONE fact is deliberate: the
 * rule that makes a row a head and the rule that decides what it opens cannot
 * drift apart, and neither depends on a filename convention the producer is
 * free to change.
 *
 * ⚠ Returns FALSE while the catalog is still loading, and false for a case with
 * no catalog at all. That is the safe direction: false means "open it the
 * ordinary way", which is what every file did before compound uploads existed.
 * The opposite default would flash the assembled-page path — and its read
 * attempt — at every file in the workspace.
 */

import { useCaseSourceCatalog } from "@/hooks/useCaseSourceCatalog";
import { nestingFactsByPath } from "@/lib/workspace/case-files-nesting";
import type { FileRef } from "@/stores/useWorkspaceFileStore";
import { useMemo } from "react";

export function useIsCompoundHead(file: FileRef | null): boolean {
  const catalog = useCaseSourceCatalog();
  const facts = useMemo(
    () => nestingFactsByPath(catalog.data?.catalog?.sources),
    [catalog.data],
  );
  if (!file) return false;
  return Boolean(facts.get(file.path)?.nodeId);
}
